/**
 * 工程表の2回目のダイアログ（#101、docs/39 §4.2、Figma B。#105 で中止を工程ごと＝Figma A3b・休みの日）。どれも「やめる」と Esc・外を押すと閉じる。
 *   見積から作る（見積の大項目 → 工程・業者・日数）／雛形から追加／前の案件からうつす／雛形として保存／中止にする／日付から後ろをまとめてずらす／休みの日
 *   金額は画面では決めない（API が DB の関数から引き直す）。画面の「並び」は contract の rowsFromItems で見せるだけ
 */
import {
  SCHEDULE_LIMITS,
  WEEKDAY_JA,
  addDays,
  addStops,
  dayNo,
  mdOf,
  mdwJaOf,
  isBaseOffDay,
  TASK_STATE_LABEL,
  taskStateOf,
  type TaskState,
  rowDayState,
  normalizeSchedule,
  rowsFromItems,
  spanText,
  stopCandidates,
  stopRuns,
  stopShifts,
  weekdayOf,
  type CompanyOffDay,
  type ScheduleDoc,
  type ScheduleAssignee,
  type ScheduleAssignees,
  type ScheduleEstimateItems,
  type ScheduleRow,
  type ScheduleSource,
  type ScheduleStop,
  type ScheduleTemplateItem,
  type StopReason,
  type StopTarget,
  type WorkCalendar,
} from "@genbaryoku/contract";
import { SCHEDULE_MSG, showsRain, stopIcon } from "./msg.js";
import { t } from "../../labels.js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, messageOf } from "../../api.js";
import { CalendarPop } from "../../components/CalendarPop.jsx";
import { CrIcon } from "../../components/Create.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Field, Switch, guardComposingEnter } from "../../components/ui.jsx";
import { toHalf, yen } from "../../lib/num.js";
import { AssigneeInput } from "./AssigneeInput.jsx";

export function useEsc(onCancel: () => void) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      // カレンダー・候補の枠が開いているときは、そちらが先に閉じる
      if (e.key === "Escape" && !document.querySelector(".calpop,.sch-asgpop")) onCancel();
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onCancel]);
}

/** 「2026/9/14(月)」 */
export const ymdw = (d: string) => `${d.slice(0, 4)}/${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}(${WEEKDAY_JA[weekdayOf(d)]})`;

/** 日付の欄（押すと大きいカレンダー。浮く名前の欄と同じ見た目） */
export function DateButton({ value, onChange, label, testId }: { value: string; onChange: (v: string) => void; label: string; testId?: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <button type="button" className="sch-datefield" aria-label={`${label} ${ymdw(value)}`} onClick={(e) => setAnchor(e.currentTarget)} data-testid={testId}>
        <span className="l">{label}</span>
        <span className="v">{ymdw(value)}</span>
        <CrIcon k="cal" />
      </button>
      {anchor ? (
        <CalendarPop
          value={value}
          anchor={anchor}
          label={label}
          onClose={() => setAnchor(null)}
          onPick={(v) => {
            setAnchor(null);
            anchor.focus();
            if (v) onChange(v);
          }}
        />
      ) : null}
    </>
  );
}

/** ダイアログの枠 */
export function Dlg({ title, onCancel, children, foot, wide, testId, icon }: { title: string; onCancel: () => void; children: ReactNode; foot: ReactNode; wide?: boolean; testId: string; icon?: ReactNode }) {
  useEsc(onCancel);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className={"dlg" + (wide ? " sch-wide" : "")} role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
        <div className="dh sch-dh">
          {icon ? <span className="sch-dhicon">{icon}</span> : null}
          {title}
          <span className="sp" />
          <button type="button" className="btn text sq" aria-label="閉じる" onClick={onCancel}>
            ×
          </button>
        </div>
        <div className="db">{children}</div>
        <div className="df">{foot}</div>
      </div>
    </div>
  );
}

/** 日数の小さな欄（全角も半角に。1〜999） */
function DaysBox({ value, onChange, label, disabled }: { value: number; onChange: (n: number) => void; label: string; disabled?: boolean }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const t = toHalf(text, "int").replace(/[,-]/g, "");
    const n = Math.max(1, Math.min(SCHEDULE_LIMITS.workDays, Number(t) || 1));
    setText(String(n));
    if (n !== value) onChange(n);
  };
  return (
    <input
      className="inp num sch-daysbox"
      type="text"
      inputMode="numeric"
      value={text}
      aria-label={label}
      disabled={disabled}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

/* ======================================================================
 * 見積から工程表を作る（Figma B）
 * ==================================================================== */
export type FromEstimateSubmit = {
  estimateId: string;
  start: string;
  chain: boolean;
  heading: string | null;
  items: { key: string; days: number; assignee: ScheduleAssignee | null }[];
};
type Pick = { on: boolean; days: number; assignee: ScheduleAssignee | null };

export function FromEstimateDialog({
  groupId,
  hasRows,
  defaultStart,
  cal,
  offText,
  cands,
  onRegister,
  onOffDays,
  onCancel,
  onSubmit,
}: {
  groupId: string;
  /** 工程表にもう行がある（「見積から追加」。見出しを付けて下に足す） */
  hasRows: boolean;
  defaultStart: string;
  cal: WorkCalendar;
  /** 休みの見出し（見るだけ）「日・祝・会社の休み」 */
  offText: string;
  cands: ScheduleAssignees | undefined;
  onRegister: (name: string) => Promise<string | null>;
  /** 休みの日を変える（ダイアログを閉じて「休みの日」を開く） */
  onOffDays: () => void;
  onCancel: () => void;
  onSubmit: (v: FromEstimateSubmit) => Promise<void>;
}) {
  const [estimateId, setEstimateId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["schedule-estimate-items", groupId, estimateId],
    queryFn: () => api<ScheduleEstimateItems>("GET", `/groups/${groupId}/schedule/estimate-items${estimateId ? `?estimateId=${estimateId}` : ""}`),
    staleTime: 0,
  });
  const data = q.data;
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  const [start, setStart] = useState(defaultStart);
  const [chain, setChain] = useState(true);
  const [headingOn, setHeadingOn] = useState(hasRows);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  // 見積を選び直したら、チェック・日数・業者を案から入れ直す
  useEffect(() => {
    if (!data) return;
    setPicks(
      Object.fromEntries(
        data.items.map((i) => [i.key, { on: i.defaultOn, days: i.suggestedDays, assignee: i.vendor ? ({ type: "partner", id: i.vendor.id, name: i.vendor.name } as ScheduleAssignee) : null }]),
      ),
    );
  }, [data]);
  const est = data?.estimates.find((e) => e.id === data.estimateId) ?? null;
  const items = data?.items ?? [];
  const on = items.filter((i) => picks[i.key]?.on);
  // 並びの見本（画面で見せるだけ。保存は API が同じ関数でそろえる）
  const preview = useMemo(() => {
    let n = 0;
    const doc = rowsFromItems(
      on.map((i) => ({ key: i.key, name: i.name, amount: null, days: picks[i.key]!.days, assignee: null })),
      { start, chain, heading: null, sourceDocumentId: null },
      cal,
      () => `p${n++}`,
    );
    return new Map(on.map((i, k) => [i.key, doc.rows[k]!]));
  }, [on.map((i) => `${i.key}:${picks[i.key]?.days}`).join(","), start, chain, cal]); // eslint-disable-line react-hooks/exhaustive-deps
  const ends = [...preview.values()].map((r) => r.end ?? r.start ?? start).sort();
  const end = ends[ends.length - 1] ?? start;
  const span = Math.max(1, (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1);
  const pos = (d: string) => ((Date.parse(d) - Date.parse(start)) / 86_400_000 / span) * 100;
  const set = (key: string, patch: Partial<Pick>) => setPicks((p) => ({ ...p, [key]: { ...p[key]!, ...patch } }));
  const title = hasRows ? SCHEDULE_MSG.fromEstimateAddTitle : SCHEDULE_MSG.fromEstimateTitle;

  const submit = async () => {
    if (!data?.estimateId || !on.length) return setErr(SCHEDULE_MSG.noItemPicked);
    setBusy(true);
    setErr(undefined);
    try {
      await onSubmit({
        estimateId: data.estimateId,
        start,
        chain,
        heading: hasRows && headingOn ? (est?.title || "見積").slice(0, SCHEDULE_LIMITS.name) : null,
        items: on.map((i) => ({ key: i.key, days: picks[i.key]!.days, assignee: picks[i.key]!.assignee })),
      });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <Dlg
      title={title}
      wide
      testId="sch-from-estimate"
      icon={<CrIcon k="wand" />}
      onCancel={onCancel}
      foot={
        <>
          <span className="sch-fesum" data-testid="sch-fe-summary">
            {on.length ? SCHEDULE_MSG.itemsSummary(on.length, mdOf(start), mdOf(end)) : SCHEDULE_MSG.noItemPicked}
          </span>
          <span className="sp" />
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={busy || !on.length} onClick={submit} data-testid="sch-fe-ok">
            {hasRows ? SCHEDULE_MSG.addFromEstimate : SCHEDULE_MSG.createFromEstimate}
            <CrIcon k="arrow" />
          </button>
        </>
      }
    >
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      <Field label="見積" id="sch-fe-est">
        <select
          id="sch-fe-est"
          className="inp"
          value={data?.estimateId ?? ""}
          onChange={(e) => setEstimateId(e.target.value)}
          disabled={!data || data.estimates.length < 2}
          data-testid="sch-fe-estimate"
        >
          {(data?.estimates ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {e.no ?? "下書き"}　{e.title || "件名なし"}　¥{yen(e.totalExTax)}
            </option>
          ))}
        </select>
      </Field>
      <div className="sch-ferow">
        <DateButton value={start} onChange={setStart} label={t("{pw:startDay}")} testId="sch-fe-start" />
        <button type="button" className="sch-datefield ro" onClick={onOffDays} aria-label={`休日 ${offText}（押すと休日を変更）`}>
          <span className="l">休日</span>
          <span className="v">{offText}</span>
          <CrIcon k="right" />
        </button>
        <Switch checked={chain} onChange={setChain} label={SCHEDULE_MSG.chain} testId="sch-fe-chain">
          <span className="swl">{SCHEDULE_MSG.chain}</span>
        </Switch>
      </div>
      {hasRows ? (
        <label className="ck sch-fehead">
          <input type="checkbox" checked={headingOn} onChange={(e) => setHeadingOn(e.target.checked)} data-testid="sch-fe-heading" />
          {SCHEDULE_MSG.headingOn(est?.title ?? "")}
        </label>
      ) : null}
      <div className="sch-fetbl" role="table" aria-label="見積の項目">
        <div className="tr th" role="row">
          <span role="columnheader" className="c-ck" />
          <span role="columnheader">見積の項目</span>
          <span role="columnheader" className="r">金額（税抜）</span>
          <span role="columnheader">{t("担当（{contractor}）")}</span>
          <span role="columnheader" className="r">日数</span>
          <span role="columnheader">並び（{mdOf(start)}〜）</span>
        </div>
        {q.isPending ? <p className="hint" style={{ margin: 12 }}>読み込み中…</p> : null}
        {items.map((i) => {
          const p = picks[i.key];
          if (!p) return null;
          const r = preview.get(i.key);
          return (
            <div key={i.key} className={"tr" + (p.on ? "" : " off")} role="row" data-testid="sch-fe-item" data-key={i.key}>
              <span role="cell" className="c-ck">
                <input type="checkbox" checked={p.on} onChange={(e) => set(i.key, { on: e.target.checked })} aria-label={t(`「${i.name || "（名前なし）"}」を{task}にする`)} />
              </span>
              <span role="cell" className="nm">{i.name || "（名前なし）"}</span>
              <span role="cell" className="r num">{i.amount < 0 ? "−" : ""}¥{yen(Math.abs(i.amount))}</span>
              {p.on ? (
                <>
                  <span role="cell" className="asg">
                    <AssigneeInput value={p.assignee} cands={cands} label={`「${i.name}」の担当`} onRegister={onRegister} onChange={(a) => set(i.key, { assignee: a })} />
                  </span>
                  <span role="cell" className="r">
                    <DaysBox value={p.days} onChange={(n) => set(i.key, { days: n })} label={`「${i.name}」の日数`} />
                  </span>
                  <span role="cell" className="bars" aria-label={r?.start ? `${mdOf(r.start)}〜${mdOf(r.end ?? r.start)}` : ""}>
                    {r?.start ? <i style={{ left: `${pos(r.start)}%`, width: `${Math.max(1.5, pos(r.end ?? r.start) - pos(r.start) + 100 / span)}%` }} /> : null}
                  </span>
                </>
              ) : (
                <span role="cell" className="not">{SCHEDULE_MSG.notTask}</span>
              )}
            </div>
          );
        })}
        {data && !items.length ? <p className="hint" style={{ margin: 12 }}>{data.estimates.length ? "この見積には項目がありません" : SCHEDULE_MSG.noEstimate}</p> : null}
      </div>
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}

/* ======================================================================
 * 雛形から追加・前の案件からうつす
 * ==================================================================== */
export function FromTemplateDialog({ defaultStart, onCancel, onSubmit }: { defaultStart: string; onCancel: () => void; onSubmit: (v: { templateId: string; start: string }) => Promise<void> }) {
  const q = useQuery({ queryKey: ["schedule-templates"], queryFn: () => api<ScheduleTemplateItem[]>("GET", "/schedule-templates"), staleTime: 0 });
  const [pick, setPick] = useState<string | null>(null);
  const [start, setStart] = useState(defaultStart);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  // 非表示にした標準テンプレートは出さない（#291）
  const list = (q.data ?? []).filter((x) => !x.hidden);
  const id = pick ?? list[0]?.id ?? null;
  return (
    <Dlg
      title={SCHEDULE_MSG.fromTemplateTitle}
      testId="sch-from-template"
      icon={<CrIcon k="layers" />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!id || busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              try {
                await onSubmit({ templateId: id!, start });
              } catch (e) {
                setErr(messageOf(e));
                setBusy(false);
              }
            }}
            data-testid="sch-ft-ok"
          >
            テンプレートから追加
          </button>
        </>
      }
    >
      {q.isPending ? <p className="hint">読み込み中…</p> : null}
      {q.data && !list.length ? <p style={{ margin: 0 }}>{SCHEDULE_MSG.noTemplate}</p> : null}
      {list.length ? (
        <div className="sch-picklist" role="radiogroup" aria-label="テンプレート">
          {list.map((t) => (
            <label key={t.id} className="ck">
              <input type="radio" name="sch-tpl" checked={id === t.id} onChange={() => setPick(t.id)} data-testid="sch-ft-pick" />
              <span>
                {t.name}
                {t.builtin ? <small className="chip" style={{ marginLeft: 6 }}>{SCHEDULE_MSG.builtin}</small> : null}
              </span>
              <small className="muted">{t.rowCount}行</small>
            </label>
          ))}
        </div>
      ) : null}
      <DateButton value={start} onChange={setStart} label={t("{pw:startDay}")} testId="sch-ft-start" />
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}

export function FromGroupDialog({ groupId, defaultStart, onCancel, onSubmit }: { groupId: string; defaultStart: string; onCancel: () => void; onSubmit: (v: { sourceGroupId: string; start: string }) => Promise<void> }) {
  const [text, setText] = useState("");
  const [q0, setQ0] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ0(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text]);
  const q = useQuery({
    queryKey: ["schedule-sources", groupId, q0],
    queryFn: () => api<ScheduleSource[]>("GET", `/schedule/sources?exclude=${groupId}${q0 ? `&q=${encodeURIComponent(q0)}` : ""}`),
    staleTime: 0,
  });
  const [pick, setPick] = useState<string | null>(null);
  const [start, setStart] = useState(defaultStart);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const list = q.data ?? [];
  const id = pick && list.some((x) => x.groupId === pick) ? pick : (list[0]?.groupId ?? null);
  return (
    <Dlg
      title={SCHEDULE_MSG.fromGroupTitle}
      testId="sch-from-group"
      icon={<CrIcon k="copy" />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!id || busy}
            onClick={async () => {
              setBusy(true);
              setErr(undefined);
              try {
                await onSubmit({ sourceGroupId: id!, start });
              } catch (e) {
                setErr(messageOf(e));
                setBusy(false);
              }
            }}
            data-testid="sch-fg-ok"
          >
            複写
          </button>
        </>
      }
    >
      <Field label={t("{project}・{customer}で検索")} id="sch-fg-q">
        <input id="sch-fg-q" className="inp" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={guardComposingEnter} autoComplete="off" />
      </Field>
      {q.data && !list.length ? <p style={{ margin: 0 }}>{SCHEDULE_MSG.noSource}</p> : null}
      {list.length ? (
        <div className="sch-picklist" role="radiogroup" aria-label={t("複写元の{schedule}")}>
          {list.map((x) => (
            <label key={x.groupId} className="ck">
              <input type="radio" name="sch-src" checked={id === x.groupId} onChange={() => setPick(x.groupId)} />
              <span>
                {x.name}
                <small className="muted">　{x.clientName}</small>
              </span>
              <small className="muted">
                {x.start && x.end ? spanText(x.start, x.end) : "日付未定"}・{x.rowCount}行
              </small>
            </label>
          ))}
        </div>
      ) : null}
      <DateButton value={start} onChange={setStart} label={t("{pw:startDay}")} testId="sch-fg-start" />
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}

/* ======================================================================
 * 雛形として保存
 * ==================================================================== */
export function SaveTemplateDialog({ defaultName, onCancel, onSubmit }: { defaultName: string; onCancel: () => void; onSubmit: (name: string) => Promise<void> }) {
  const [name, setName] = useState(defaultName.slice(0, SCHEDULE_LIMITS.templateName));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const submit = async () => {
    if (!name.trim()) return setErr("名前を入力してください");
    setBusy(true);
    setErr(undefined);
    try {
      await onSubmit(name.trim());
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <Dlg
      title={SCHEDULE_MSG.templateSaveTitle}
      testId="sch-save-template"
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={submit} data-testid="sch-st-ok">
            テンプレート保存
          </button>
        </>
      }
    >
      <Field label={SCHEDULE_MSG.templateName} id="sch-st-name">
        <input
          id="sch-st-name"
          className="inp"
          value={name}
          maxLength={SCHEDULE_LIMITS.templateName}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            guardComposingEnter(e);
            if (e.key === "Enter" && !e.defaultPrevented) void submit();
          }}
        />
      </Field>
      <ErrorText>{err}</ErrorText>
    </Dlg>
  );
}

/* ======================================================================
 * 中止・休み（#298 で「雨で中止」「中止の確認」から。#105 で工程ごと、Figma A3b）。主ボタンは「登録」。
 *   日付（から・まで）／対象＝「工程を選ぶ」（既定。「雨でも作業する」工程は最初から外す）か「現場全体」（台風など）／
 *   「この日にかかる工程」の一覧／理由（雨・ほか＋メモ）／結果の1行（止めたらどこがずれるか）。下にこの工程表の中止の一覧と「取り消す」
 *   rowId があれば（工程の ⋮「工程中止」）その工程だけチェックして開く
 * ==================================================================== */
export function StopsDialog({
  doc,
  calOf,
  defaultDate,
  rowId,
  onCancel,
  onAdd,
  onRemove,
}: {
  doc: ScheduleDoc & { stops: ScheduleStop[] };
  /** 中止を入れた暦（結果の1行に使う。画面と同じ暦） */
  calOf: (stops: ScheduleStop[]) => WorkCalendar;
  defaultDate: string;
  rowId?: string | undefined;
  onCancel: () => void;
  onAdd: (next: ScheduleStop[], from: string, to: string, target: StopTarget) => void;
  onRemove: (dates: string[]) => void;
}) {
  const [from, setFrom] = useState(defaultDate);
  const [to, setTo] = useState(defaultDate);
  const [scope, setScope] = useState<"rows" | "all">("rows");
  // 選んだ工程（null＝まださわっていない＝その期間の既定のチェック）
  const [picked, setPicked] = useState<Set<string> | null>(() => (rowId ? new Set([rowId]) : null));
  // わけの選択（雨／ほか）は現場力だけ。営業力は選ばずに「ほか」＝メモだけ（#142 段5 No.126）
  const rain = showsRain();
  const [reason, setReason] = useState<StopReason>(rain ? "rain" : "other");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const runs = stopRuns(doc.stops);
  const nameOf = useMemo(() => new Map(doc.rows.map((r) => [r.id, r.name || "（名前なし）"])), [doc.rows]);
  const cal = useMemo(() => calOf(doc.stops), [doc.stops]); // eslint-disable-line react-hooks/exhaustive-deps
  const cands = useMemo(() => (from <= to ? stopCandidates(doc, from, to, cal) : []), [doc, from, to, cal]);
  const chosen = picked ?? new Set(cands.filter((c) => c.checked).map((c) => c.row.id));
  const ids = cands.filter((c) => chosen.has(c.row.id)).map((c) => c.row.id);
  // {siteShort}の休み（#293）は{siteShort}全体だけ（対象の選択を出さない）
  const off = reason === "off";
  const target: StopTarget = scope === "all" || off ? { scope: "all" } : { scope: "rows", rowIds: ids };
  const spanOk = from <= to && dayNo(to) - dayNo(from) + 1 <= SCHEDULE_LIMITS.stopSpan;
  // 結果の1行：止めたらずれる行（止めた工程のほか。後ろの工程・節目）
  const preview = useMemo(() => {
    if (!spanOk || (target.scope === "rows" && !ids.length)) return null;
    const next = addStops(doc.stops, from, to, reason, null, target);
    const c2 = calOf(next);
    const after = normalizeSchedule({ rows: doc.rows, links: doc.links }, c2);
    const stopped = new Set(target.scope === "all" ? cands.map((c) => c.row.id) : ids);
    const sh = stopShifts(doc.rows, after.rows, stopped, c2);
    const names = sh.rows.slice(0, 2).map((r) => (r.kind === "milestone" ? `「${r.name || "節目"}」` : r.name || "（名前なし）"));
    const what = target.scope === "all" ? SCHEDULE_MSG.stopPreviewAll(cands.length) : SCHEDULE_MSG.stopPreviewRows(ids.length);
    return SCHEDULE_MSG.stopPreview(what, names, Math.max(0, sh.rows.length - 2), sh.days, sh.same);
  }, [doc, from, to, scope, ids.join(","), cands, reason, spanOk]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = () => {
    if (to < from) return setErr(SCHEDULE_MSG.stopOrder);
    if (dayNo(to) - dayNo(from) + 1 > SCHEDULE_LIMITS.stopSpan) return setErr(SCHEDULE_MSG.stopSpanOver);
    if (target.scope === "rows" && !ids.length) return setErr(SCHEDULE_MSG.stopPickRows);
    const next = addStops(doc.stops, from, to, reason, reason === "other" || off ? note : null, target);
    if (next.length > SCHEDULE_LIMITS.stops) return setErr(SCHEDULE_MSG.stopTooMany);
    onAdd(next, from, to, target);
  };
  const toggle = (id: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
    setErr(undefined);
  };
  const allOn = cands.length > 0 && ids.length === cands.length;
  const scopeCard = (k: "rows" | "all", title: string, sub: string, icon: ReactNode) => (
    <button
      type="button"
      role="radio"
      aria-checked={scope === k}
      className={"sch-scope" + (scope === k ? " on" : "")}
      onClick={() => {
        setScope(k);
        setErr(undefined);
      }}
      data-testid={`sch-stop-scope-${k}`}
    >
      <span className="ic" aria-hidden="true">
        {icon}
      </span>
      <span className="tt">
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      <span className="rd" aria-hidden="true" />
    </button>
  );
  return (
    <Dlg
      title={SCHEDULE_MSG.stopTitle}
      testId="sch-stops"
      icon={<CrIcon k={stopIcon()} />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" onClick={submit} data-testid="sch-stop-ok">
            {SCHEDULE_MSG.stopSubmit}
          </button>
        </>
      }
    >
      <div className="sch-ferow">
        <DateButton
          value={from}
          onChange={(v) => {
            setFrom(v);
            if (to < v) setTo(v);
            setErr(undefined);
          }}
          label={SCHEDULE_MSG.stopFrom}
          testId="sch-stop-from"
        />
        <DateButton
          value={to}
          onChange={(v) => {
            setTo(v);
            setErr(undefined);
          }}
          label={SCHEDULE_MSG.stopTo}
          testId="sch-stop-to"
        />
      </div>
      {off ? null : (
      <div className="sch-scopes" role="radiogroup" aria-label="中止の対象">
        {scopeCard(
          "rows",
          SCHEDULE_MSG.stopScopeRows,
          SCHEDULE_MSG.stopScopeRowsSub,
          <svg viewBox="0 0 24 24">
            <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />
          </svg>,
        )}
        {scopeCard(
          "all",
          SCHEDULE_MSG.stopScopeAll,
          SCHEDULE_MSG.stopScopeAllSub,
          <svg viewBox="0 0 24 24">
            <path d="M3 8h11a3 3 0 1 0-3-3M3 12h15a3 3 0 1 1-3 3M3 16h8" />
          </svg>,
        )}
      </div>
      )}
      {scope === "rows" && !off ? (
        <div className="sch-stopcands">
          <div className="hd">
            <b>{SCHEDULE_MSG.stopTargets}</b>
            <span className="sp" />
            {cands.length ? (
              <button
                type="button"
                className="btn text"
                onClick={() => setPicked(allOn ? new Set() : new Set(cands.map((c) => c.row.id)))}
                data-testid="sch-stop-pickall"
              >
                {allOn ? SCHEDULE_MSG.stopPickNone : SCHEDULE_MSG.stopPickAll}
              </button>
            ) : null}
          </div>
          {cands.length ? (
            <div className="sch-candlist">
              {cands.map(({ row }) => (
                <label key={row.id} className={"ck" + (chosen.has(row.id) ? "" : " off")} data-testid="sch-stop-cand" data-row-id={row.id}>
                  <input type="checkbox" checked={chosen.has(row.id)} onChange={(e) => toggle(row.id, e.target.checked)} />
                  <span className="nm">
                    <b>
                      {row.name || "（名前なし）"}
                      {row.rainOk && rain ? <span className="sch-offwork sch-rainok">{SCHEDULE_MSG.rainOk}</span> : null}
                    </b>
                    {row.assignee ? <small>{row.assignee.type === "text" ? row.assignee.text : (row.assignee.name ?? "")}</small> : null}
                  </span>
                  <span className="pd">{spanText(row.start!, row.end!)}</span>
                </label>
              ))}
            </div>
          ) : (
            <p className="hint" style={{ margin: 0 }} data-testid="sch-stop-nocand">
              {SCHEDULE_MSG.stopNoTargets}
            </p>
          )}
        </div>
      ) : null}
      <div className="sch-reason" role="radiogroup" aria-label="理由">
        <b>理由</b>
        {rain ? (
          <label className="ck">
            <input type="radio" name="sch-reason" checked={reason === "rain"} onChange={() => setReason("rain")} />
            {SCHEDULE_MSG.stopReasonRain}
          </label>
        ) : null}
        <label className="ck">
          <input type="radio" name="sch-reason" checked={reason === "other"} onChange={() => setReason("other")} data-testid="sch-stop-other" />
          {/* 営業力は雨を選ばない＝「その他」でなく「中止」（#142 段5 No.126） */}
          {rain ? SCHEDULE_MSG.stopReasonOther : SCHEDULE_MSG.stopDo}
        </label>
        {/* {siteShort}の休み（#293。予定として入らない日。期間でまとめて入れるとき・スマホ用） */}
        <label className="ck">
          <input type="radio" name="sch-reason" checked={off} onChange={() => setReason("off")} data-testid="sch-stop-off" />
          {SCHEDULE_MSG.stopReasonOff}
        </label>
        {reason === "other" || off ? (
          <input className="inp sch-stopnote" value={note} maxLength={SCHEDULE_LIMITS.stopNote} aria-label="メモ" onChange={(e) => setNote(e.target.value)} onKeyDown={guardComposingEnter} data-testid="sch-stop-note" />
        ) : null}
      </div>
      {preview ? (
        <p className="sch-stoppreview" role="status" data-testid="sch-stop-preview">
          {preview}
        </p>
      ) : null}
      <ErrorText>{err}</ErrorText>
      <div className="sch-stoplist" aria-label={SCHEDULE_MSG.stopList}>
        <b>{SCHEDULE_MSG.stopList}</b>
        {runs.length ? (
          runs.map((r) => (
            <div key={r.from} className="it" data-testid="sch-stop-item">
              <span className={"mk" + (r.reason === "off" ? " off" : "")} aria-hidden="true">{r.reason === "off" ? SCHEDULE_MSG.siteOffMark : r.reason === "rain" && rain ? "☂" : "止"}</span>
              <span className="tx">
                {/* 日付と理由のあいだに空白（#298。前は改行の前の全角の空白が JSX で消えて「11/29現場の休み」） */}
                {spanText(r.from, r.to)}{" "}
                {r.reason === "off"
                  ? r.note || SCHEDULE_MSG.stopReasonOff
                  : r.reason === "rain" && rain
                    ? SCHEDULE_MSG.stopReasonRain
                    : r.note || (rain ? SCHEDULE_MSG.stopReasonOther : "")}
                <small>{r.scope === "all" ? SCHEDULE_MSG.stopAllRows : r.rowIds.map((id) => nameOf.get(id) ?? "").join("・")}</small>
              </span>
              <span className="sp" />
              <button type="button" className="btn text" onClick={() => onRemove(r.dates)} aria-label={`${spanText(r.from, r.to)} の中止を解除`}>
                解除
              </button>
            </div>
          ))
        ) : (
          <p className="hint" style={{ margin: 0 }}>{SCHEDULE_MSG.stopNone}</p>
        )}
      </div>
    </Dlg>
  );
}

/* ======================================================================
 * 休みの日（工程表の ⋮ から。#105 で操作の列から ⋮ へ移した）。変えるとすぐ保存
 * ==================================================================== */
export function OffDaysDialog({
  offWeekdays,
  holidayOff,
  useCompanyOffDays,
  companyOff,
  workDays = [],
  cal,
  onClose,
  onChange,
}: {
  offWeekdays: readonly number[];
  holidayOff: boolean;
  useCompanyOffDays: boolean;
  companyOff: readonly CompanyOffDay[];
  /** {siteShort}の作業日（#296。日付の順）と、選べる日を決める今の暦（無ければ節を出さない） */
  workDays?: readonly string[];
  cal?: WorkCalendar;
  onClose: () => void;
  onChange: (patch: { offWeekdays?: number[]; holidayOff?: boolean; useCompanyOffDays?: boolean; workDays?: string[] }) => void;
}) {
  const off = new Set(offWeekdays);
  const [pick, setPick] = useState<HTMLElement | null>(null);
  const [workErr, setWorkErr] = useState("");
  return (
    <Dlg
      title="休日"
      testId="sch-offdays-dlg"
      icon={<CrIcon k="cal" />}
      onCancel={onClose}
      foot={
        <button type="button" className="btn primary" onClick={onClose} data-testid="sch-offdays-close">
          閉じる
        </button>
      }
    >
      <div className="sch-offwds" role="group" aria-label="休日の曜日" data-testid="sch-offdays">
        {WEEKDAY_JA.map((w, k) => (
          <label key={w} className="ck">
            <input
              type="checkbox"
              checked={off.has(k)}
              disabled={!off.has(k) && off.size >= 6}
              onChange={(e) => {
                const next = new Set(off);
                if (e.target.checked) next.add(k);
                else next.delete(k);
                onChange({ offWeekdays: [...next].sort() });
              }}
            />
            {w}
          </label>
        ))}
      </div>
      <label className="ck">
        <input type="checkbox" checked={holidayOff} onChange={(e) => onChange({ holidayOff: e.target.checked })} data-testid="sch-holiday-off" />
        祝日も休日
      </label>
      {companyOff.length ? (
        <label className="ck">
          <input type="checkbox" checked={useCompanyOffDays} onChange={(e) => onChange({ useCompanyOffDays: e.target.checked })} data-testid="sch-company-off" />
          {SCHEDULE_MSG.useCompanyOffDays}（{companyOff.map((o) => o.name || spanText(o.from, o.to)).join("・")}）
        </label>
      ) : null}
      {/* 作業日にした休日（#296、docs/102 §5.8 の5）：日付の見出しを押すのと同じ（ドラッグしない方法・スマホとタブレットもここ） */}
      {cal ? (
        <div className="sch-workdays" role="group" aria-label={SCHEDULE_MSG.workDaysSection} data-testid="sch-workdays">
          <div className="sch-workdays-h">
            <b>{SCHEDULE_MSG.workDaysSection}</b>
            <span className="sp" />
            <button type="button" className="btn tonal" onClick={(e) => (setWorkErr(""), setPick(e.currentTarget))} data-testid="sch-workday-add">
              {SCHEDULE_MSG.workDaysAdd}
            </button>
          </div>
          {workDays.length ? (
            <ul className="sch-workdays-list">
              {workDays.map((d) => (
                <li key={d} data-testid="sch-workday" data-date={d}>
                  <span>{mdwJaOf(d, cal.holidays)}</span>
                  <button
                    type="button"
                    className="btn text sq"
                    aria-label={SCHEDULE_MSG.workDaysRemove(mdwJaOf(d, cal.holidays))}
                    onClick={() => onChange({ workDays: workDays.filter((x) => x !== d) })}
                    data-testid="sch-workday-remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="sch-workdays-none">{SCHEDULE_MSG.workDaysNone}</p>
          )}
          <ErrorText>{workErr}</ErrorText>
          {pick ? (
            <CalendarPop
              value={null}
              anchor={pick}
              label={SCHEDULE_MSG.workDaysPick}
              onClose={() => setPick(null)}
              onPick={(v) => {
                pick.focus();
                setPick(null);
                if (!v || workDays.includes(v)) return;
                if (!isBaseOffDay(v, cal) || cal.stops?.has(v)) {
                  setWorkErr(SCHEDULE_MSG.workDaysNotOff);
                  return;
                }
                onChange({ workDays: [...workDays, v].sort() });
              }}
            />
          ) : null}
        </div>
      ) : null}
    </Dlg>
  );
}

/* ======================================================================
 * 表示設定（#298、docs/102 §2.4.2）：帯の ⋮「表示設定」。前の ⋮「表の列」（#296）・「バーの日付を常に表示」（#291）・「表の幅：標準／広い／自動幅」（#296）を1つに。
 *   列＝開始・日数・状況（％の会社は進捗）・人のチェック／表の幅＝標準・広い・自動（手で幅を変えたときはどれも選ばれていない）／バーの日付＝常に表示のスイッチ。
 *   押すたびにすぐ効く（「閉じる」だけ）。覚える場所は今のまま（gb.sch.cols・gb.sch.crewCol・gb.sch.nameWidth・gb.sch.barDates＝その人のブラウザ）
 * ==================================================================== */
export type DisplayWidth = "std" | "wide" | "auto" | "manual";
export function DisplayDialog({
  cols,
  crew,
  pct,
  width,
  barDates,
  onChange,
  onCrew,
  onWidth,
  onBarDates,
  onClose,
}: {
  cols: { start: boolean; days: boolean; state: boolean };
  /** 人の列が今見えているか（#297 から既定は出さない＝ON にした人だけ） */
  crew: boolean;
  pct: boolean;
  /** 表の幅（manual＝境をドラッグして変えた幅＝どれも選ばれていない） */
  width: DisplayWidth;
  barDates: boolean;
  onChange: (next: { start: boolean; days: boolean; state: boolean }) => void;
  onCrew: (on: boolean) => void;
  onWidth: (w: "std" | "wide" | "auto") => void;
  onBarDates: (on: boolean) => void;
  onClose: () => void;
}) {
  const items: { key: "start" | "days" | "state"; label: string }[] = [
    { key: "start", label: SCHEDULE_MSG.colStart },
    { key: "days", label: SCHEDULE_MSG.colDays },
    { key: "state", label: pct ? SCHEDULE_MSG.colProgress : SCHEDULE_MSG.colState },
  ];
  return (
    <Dlg
      title={SCHEDULE_MSG.viewTitle}
      testId="sch-display-dlg"
      onCancel={onClose}
      foot={
        <button type="button" className="btn primary" onClick={onClose} data-testid="sch-display-close">
          閉じる
        </button>
      }
    >
      <div className="sch-disp">
        <span className="sch-disp-h" id="sch-disp-cols">{SCHEDULE_MSG.viewCols}</span>
        <div className="sch-cols" role="group" aria-labelledby="sch-disp-cols">
          {items.map((x) => (
            <label key={x.key} className="ck">
              <input type="checkbox" checked={cols[x.key]} onChange={(e) => onChange({ ...cols, [x.key]: e.target.checked })} data-testid={`sch-col-${x.key}`} />
              {x.label}
            </label>
          ))}
          <label className="ck">
            <input type="checkbox" checked={crew} onChange={(e) => onCrew(e.target.checked)} data-testid="sch-col-crew" />
            {SCHEDULE_MSG.colCrew}
          </label>
        </div>
        <span className="sch-disp-h" id="sch-disp-width">{SCHEDULE_MSG.viewWidth}</span>
        <Segmented<DisplayWidth>
          labelledBy="sch-disp-width"
          value={width}
          onChange={(v) => {
            if (v !== "manual") onWidth(v);
          }}
          testId="sch-display-width"
          options={(
            [
              ["std", SCHEDULE_MSG.viewWidthStd],
              ["wide", SCHEDULE_MSG.viewWidthWide],
              ["auto", SCHEDULE_MSG.viewWidthAuto],
            ] as const
          ).map(([value, label]) => ({ value, label, testId: `sch-width-${value}` }))}
        />
        <span className="sch-disp-h">{SCHEDULE_MSG.viewBarDates}</span>
        <Switch checked={barDates} onChange={onBarDates} label={SCHEDULE_MSG.viewBarDatesAlways} testId="sch-display-bardates" />
      </div>
    </Dlg>
  );
}

/* ======================================================================
 * 行の「開始・日数」「状況」（#296）・「人数」（#297。#298 で「…」をやめた）：表の列を隠したときのドラッグしない方法（行の ⋮ から）。押すたびにすぐ効く
 * ==================================================================== */
export function RowFieldsDialog({
  row,
  mode,
  pct,
  today,
  onDates,
  onState,
  onPct,
  onCrew,
  onClose,
}: {
  row: ScheduleRow;
  mode: "dates" | "state" | "crew";
  pct: boolean;
  today: string;
  onDates: (patch: { start?: string; days?: number }) => void;
  onState: (to: TaskState) => void;
  onPct: (n: number) => void;
  /** 人数（#297）：1〜99。空にすると人数なし */
  onCrew: (n: number | null) => void;
  onClose: () => void;
}) {
  const ms = row.kind === "milestone";
  const [pctText, setPctText] = useState(String(row.progress ?? 0));
  useEffect(() => setPctText(String(row.progress ?? 0)), [row.progress]);
  const [crewText, setCrewText] = useState(row.crew == null ? "" : String(row.crew));
  useEffect(() => setCrewText(row.crew == null ? "" : String(row.crew)), [row.crew]);
  const title =
    mode === "dates" ? (ms ? SCHEDULE_MSG.rowDateTitle : SCHEDULE_MSG.rowDatesTitle) : mode === "crew" ? SCHEDULE_MSG.crewLabel : pct ? SCHEDULE_MSG.colProgress : SCHEDULE_MSG.rowStateTitle;
  const st = taskStateOf(row);
  const commitPct = () => {
    const n = Math.max(0, Math.min(100, Number(toHalf(pctText, "int").replace(/[,-]/g, "")) || 0));
    setPctText(String(n));
    if (n !== (row.progress ?? 0)) onPct(n);
  };
  // 人数の欄（Gantt の CrewInput と同じ決まり）：全角も半角に・1〜99 に収める・空は人数なし
  const commitCrew = () => {
    const raw = toHalf(crewText, "int").replace(/[^\d]/g, "");
    const n = raw === "" ? null : Math.max(1, Math.min(SCHEDULE_LIMITS.crewMax, Number(raw)));
    setCrewText(n == null ? "" : String(n));
    if (n !== (row.crew ?? null)) onCrew(n);
  };
  return (
    <Dlg
      title={`${title}　${row.name || t("{task}")}`}
      testId="sch-rowfields-dlg"
      onCancel={onClose}
      foot={
        <button type="button" className="btn primary" onClick={onClose} data-testid="sch-rowfields-close">
          閉じる
        </button>
      }
    >
      {mode === "dates" ? (
        <div className="sch-ferow">
          <DateButton value={row.start ?? today} onChange={(v) => onDates({ start: v })} label={SCHEDULE_MSG.colStart} testId="sch-rowfields-start" />
          {ms ? null : (
            <label className="sch-shiftin">
              <DaysBox value={row.days ?? 1} onChange={(n) => onDates({ days: n })} label={SCHEDULE_MSG.colDays} />
              日
            </label>
          )}
        </div>
      ) : mode === "crew" ? (
        <label className="sch-shiftin">
          <input
            className="inp num sch-daysbox"
            type="text"
            inputMode="numeric"
            value={crewText}
            aria-label={SCHEDULE_MSG.crewLabel}
            onChange={(e) => setCrewText(e.target.value)}
            onBlur={commitCrew}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
                e.preventDefault();
                commitCrew();
              }
            }}
            data-testid="sch-rowfields-crew"
          />
          人
        </label>
      ) : pct ? (
        <label className="sch-shiftin">
          <input
            className="inp num sch-daysbox"
            type="text"
            inputMode="numeric"
            value={pctText}
            aria-label={SCHEDULE_MSG.colProgress}
            onChange={(e) => setPctText(e.target.value)}
            onBlur={commitPct}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
                e.preventDefault();
                commitPct();
              }
            }}
            data-testid="sch-rowfields-pct"
          />
          %
        </label>
      ) : (
        <Segmented<TaskState>
          className="is-wide"
          label={SCHEDULE_MSG.rowStateTitle}
          value={st}
          onChange={onState}
          options={(["todo", "started", "done"] as const).map((k) => ({ value: k, label: TASK_STATE_LABEL[k], testId: `sch-rowfields-state-${k}` }))}
        />
      )}
    </Dlg>
  );
}

/* ======================================================================
 * 日付から後ろをまとめてずらす
 * ==================================================================== */
export function ShiftFromDialog({ defaultDate, onCancel, onOk }: { defaultDate: string; onCancel: () => void; onOk: (from: string, n: number) => void }) {
  const [from, setFrom] = useState(defaultDate);
  const [text, setText] = useState("1");
  const n = Number(text.normalize("NFKC").replace(/[−–—ー―‐]/g, "-").replace(/[^\d-]/g, "")) || 0;
  return (
    <Dlg
      title={SCHEDULE_MSG.shiftFromTitle}
      testId="sch-shift-from"
      icon={<CrIcon k="shift" />}
      onCancel={onCancel}
      foot={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={!n || Math.abs(n) > SCHEDULE_LIMITS.workDays} onClick={() => onOk(from, n)} data-testid="sch-shift-from-ok">
            移動
          </button>
        </>
      }
    >
      <div className="sch-ferow">
        <DateButton value={from} onChange={setFrom} label={SCHEDULE_MSG.shiftFromDate} testId="sch-shift-from-date" />
        <label className="sch-shiftin">
          <input className="inp num" type="text" inputMode="numeric" value={text} onChange={(e) => setText(e.target.value)} aria-label={SCHEDULE_MSG.shiftFromDays} autoFocus />
          日（前は −）
        </label>
      </div>
    </Dlg>
  );
}

/* ======================================================================
 * 日ごとの休み・作業（#293、docs/102 §5.4）：{task}の ⋮ から。開始〜終わりの暦で日ごとに 作業／休み を切り替える
 *   （バーの日をクリックするのと同じ。ドラッグしない方法＝タブレット・スマホの編集もここ）。押すとすぐ保存（元に戻すで戻せる）。
 *   中止・{siteShort}の休みの日は押せず、理由を薄字で。既定の休み（休日・祝日・会社の休み）は理由を薄字で出し、押すと作業にできる
 * ==================================================================== */
export function RowDaysDialog({ row, cal, onToggle, onClose }: { row: ScheduleRow; cal: WorkCalendar; onToggle: (date: string) => void; onClose: () => void }) {
  const start = row.start;
  const end = row.end ?? row.start;
  // 月ごとの暦（開始の月の1日〜終わりの月の末日。範囲の外の日は空）
  const months = useMemo(() => {
    if (!start || !end) return [];
    const out: { ym: string; cells: (string | null)[] }[] = [];
    let m = `${start.slice(0, 7)}-01`;
    while (m <= end) {
      const cells: (string | null)[] = new Array<string | null>(weekdayOf(m)).fill(null);
      for (let d = m; d.slice(0, 7) === m.slice(0, 7); d = addDays(d, 1)) cells.push(d);
      out.push({ ym: m.slice(0, 7), cells });
      m = addDays(`${m.slice(0, 7)}-01`, 40).slice(0, 7) + "-01";
    }
    return out;
  }, [start, end]);
  const whyText = (why: string | null) =>
    why === "siteOff" ? SCHEDULE_MSG.rowDayWhySiteOff : why === "stop" ? SCHEDULE_MSG.rowDayWhyStop : why === "holiday" ? SCHEDULE_MSG.rowDayWhyHoliday : why === "company" ? SCHEDULE_MSG.rowDayWhyCompany : why === "weekday" ? SCHEDULE_MSG.rowDayWhyWeekday : "";
  return (
    <Dlg
      title={SCHEDULE_MSG.rowDaysTitle}
      testId="sch-rowdays"
      onCancel={onClose}
      foot={
        <button type="button" className="btn primary" onClick={onClose} data-testid="sch-rowdays-close">
          {SCHEDULE_MSG.close}
        </button>
      }
    >
      <p className="sch-rdname">
        <b>{row.name || "（名前なし）"}</b>
        {start && end ? <span className="muted">{spanText(start, end)}・{row.days ?? 1}日</span> : null}
      </p>
      {months.map((mo) => (
        <div key={mo.ym} className="sch-rdmonth">
          <b className="ym">{`${Number(mo.ym.slice(0, 4))}年${Number(mo.ym.slice(5, 7))}月`}</b>
          <div className="sch-rdcal" role="group" aria-label={`${Number(mo.ym.slice(5, 7))}月`}>
            {WEEKDAY_JA.map((w, i) => (
              <span key={w} className={"wd" + (i === 0 ? " sun" : i === 6 ? " sat" : "")} aria-hidden="true">
                {w}
              </span>
            ))}
            {mo.cells.map((d, i) => {
              if (!d || !start || !end || d < start || d > end) return <span key={d ?? `e${i}`} className="cell out" aria-hidden="true">{d ? Number(d.slice(8)) : ""}</span>;
              const st = rowDayState(row, d, cal);
              const locked = st.why === "stop" || st.why === "siteOff";
              const reason = locked ? whyText(st.why) : st.baseWhy ? whyText(st.baseWhy) : "";
              const label = `${mdwJaOf(d, cal.holidays)} ${st.work ? SCHEDULE_MSG.rowDaysWork : SCHEDULE_MSG.rowDaysOff}${reason ? `（${reason}）` : ""}`;
              return (
                <button
                  key={d}
                  type="button"
                  className={"cell" + (st.work ? " is-on" : " is-off") + (st.why === "set" ? " is-set" : "")}
                  disabled={locked}
                  aria-pressed={st.work}
                  aria-label={label}
                  title={label}
                  onClick={() => onToggle(d)}
                  data-testid="sch-rowday"
                  data-date={d}
                  data-work={st.work ? "1" : "0"}
                >
                  <b>{Number(d.slice(8))}</b>
                  <span className="st">{st.work ? SCHEDULE_MSG.rowDaysWork : SCHEDULE_MSG.rowDaysOff}</span>
                  {reason ? <small>{reason}</small> : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </Dlg>
  );
}
